// src/hooks/useScrollRestoration.js
//
// Remember window scrollY per route so navigating from a list → detail →
// back lands the user exactly where they were. Uses sessionStorage so it
// resets on tab close (a full reload should feel like a fresh start).
//
// Wire once in App.js (or any shell that mounts under <Router>). Opt in
// per-path by providing your own path filter — by default it remembers
// every pathname.
//
// Browsers have native "scroll restoration" that sometimes does the job
// too, but it's inconsistent with SPA navigation and flaky with lazy-
// loaded routes that render an initial empty state before data arrives.
// This hook bridges that gap by waiting a frame after render + content
// mutation before restoring.

import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const KEY_PREFIX = 'caro.portal.scroll.';

const load = (path) => {
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + path);
    return raw ? parseInt(raw, 10) : null;
  } catch {
    return null;
  }
};

const save = (path, y) => {
  try {
    if (y <= 0) sessionStorage.removeItem(KEY_PREFIX + path);
    else sessionStorage.setItem(KEY_PREFIX + path, String(y));
  } catch {
    /* ignore */
  }
};

export const useScrollRestoration = ({ enabled = true, shouldRemember } = {}) => {
  const location = useLocation();
  const navType = useNavigationType(); // PUSH | POP | REPLACE
  const prevPathRef = useRef(location.pathname + location.search);

  // Save the outgoing scroll before the pathname changes.
  useEffect(() => {
    if (!enabled) return undefined;
    const current = prevPathRef.current;
    return () => {
      if (!shouldRemember || shouldRemember(current)) {
        save(current, window.scrollY || 0);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, enabled]);

  // Restore on entry. Push → top (new destination). Pop → saved. Replace → saved.
  useEffect(() => {
    if (!enabled) return;
    const path = location.pathname + location.search;
    prevPathRef.current = path;

    if (navType === 'PUSH') {
      window.scrollTo(0, 0);
      return;
    }

    const saved = load(path);
    if (saved == null) return;

    // Wait a frame so React has committed the new DOM, then a second tick
    // to let lazy-loaded content push the page height up enough for the
    // scroll to take effect.
    let cancelled = false;
    const tryScroll = (attempt = 0) => {
      if (cancelled) return;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll >= saved || attempt > 10) {
        window.scrollTo({ top: saved, behavior: 'auto' });
        return;
      }
      setTimeout(() => tryScroll(attempt + 1), 50);
    };
    requestAnimationFrame(() => tryScroll(0));
    // eslint-disable-next-line consistent-return
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, enabled, navType]);
};

export default useScrollRestoration;
